import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Button,
  Tooltip,
  TextField,
  FormLabel,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { ReactComponent as CloseIcon } from '../../../assets/SVG/close-circle-greay.svg';
import {
  filterInputSubLabelSx,
  modalCloseIconSx,
  modalHeaderSx,
  modalSaveSx,
  modalCloseSx,
} from '../../Inventory/Common/inventory.helper';
import type { SalesReturnFilterValues } from './SalesReturnHelper';

interface SalesReturnFilterModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: SalesReturnFilterValues) => void;
  initialValues?: SalesReturnFilterValues;
}

const boxContainerSx = {
  width: 480,
  bgcolor: 'background.paper',
  borderRadius: 4,
  mx: 'auto',
  mt: '10%',
  boxShadow: 24,
  outline: 'none',
  overflow: 'hidden',
};

const SalesReturnFilterModal: React.FC<SalesReturnFilterModalProps> = ({
  open,
  onClose,
  onSave,
  initialValues,
}) => {
  const { t } = useTranslation('sales_return');
  const defaultValues: SalesReturnFilterValues = {
    fromDate: initialValues?.fromDate || '',
    toDate: initialValues?.toDate || '',
    settlementStatus: initialValues?.settlementStatus || 'unsettled',
  };

  const handleClearAll = (resetForm: () => void) => {
    resetForm();
    onSave({
      fromDate: '',
      toDate: '',
      settlementStatus: 'unsettled',
    });
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
    >
      <Box data-testid="sales-return-filter-modal" sx={boxContainerSx}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          px={2}
          mb={2}
          borderBottom={'1px solid rgba(226, 227, 229, 1)'}
        >
          <Typography sx={modalHeaderSx}>{t('Filter_Title')}</Typography>
          <Tooltip title={t('Tooltip_Close')} arrow>
            <IconButton onClick={onClose} sx={modalCloseIconSx}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Formik
          initialValues={defaultValues}
          onSubmit={(values) => {
            onSave(values);
            onClose();
          }}
          enableReinitialize
        >
          {({ values, handleChange, resetForm }) => (
            <Form>
              <Box p={3} pt={1}>
                {/* From Date */}
                <Box mb={3}>
                  <TextField
                    name="fromDate"
                    label={t('From_Date')}
                    type="date"
                    size="small"
                    value={values.fromDate}
                    onChange={handleChange}
                    sx={filterInputSubLabelSx}
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        inputProps: {
                          max: values.toDate || undefined,
                        },
                      },
                    }}
                  />
                </Box>

                {/* To Date */}
                <Box mb={3}>
                  <TextField
                    name="toDate"
                    label={t('To_Date')}
                    type="date"
                    size="small"
                    value={values.toDate}
                    onChange={handleChange}
                    sx={filterInputSubLabelSx}
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        inputProps: {
                          min: values.fromDate || undefined,
                        },
                      },
                    }}
                  />
                </Box>

                {/* Settlement Status */}
                <Box mb={3}>
                  <FormLabel 
                    component="legend"
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'rgba(35, 35, 35, 1)',
                      mb: 1.5,
                      display: 'block',
                    }}
                  >
                    {t('Settlement_Status')}
                  </FormLabel>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                  >
                    {[
                      { label: t('Settled'), value: 'settled' },
                      { label: t('Unsettled'), value: 'unsettled' },
                    ].map((option) => (
                      <Box
                        key={option.value}
                        component="label"
                        sx={{
                          fontFamily: 'Montserrat',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          lineHeight: '1.07rem',
                          color: 'rgba(35, 35, 35, 1)',
                          marginRight: '0.625rem',
                          textWrap: 'nowrap',
                          cursor: 'pointer',
                          '& input[type=radio]': {
                            appearance: 'none',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundClip: 'content-box',
                            border: '2px solid #BBBBBB',
                            backgroundColor: '#FFFFFF',
                            cursor: 'pointer',
                            '&:checked': {
                              backgroundColor: '#E17E61',
                              padding: '2px',
                              border: '2px solid #000',
                            },
                          },
                        }}
                      >
                        <input
                          type="radio"
                          name="settlementStatus"
                          value={option.value}
                          checked={values.settlementStatus === option.value}
                          onChange={handleChange}
                        />
                        {option.label}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                display="flex"
                justifyContent="space-between"
                gap={2}
                p={3}
                pt={0}
              >
                <Button
                  variant="outlined"
                  onClick={() => handleClearAll(resetForm)}
                  sx={modalCloseSx}
                >
                  {t('Clear_All')}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  sx={modalSaveSx}
                >
                  {t('Apply')}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default SalesReturnFilterModal;
